import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Typography, Box, Button, Snackbar } from '@gapartners/ui';
import { api } from '@/store/api';
import type { MissedCallItem } from '@/types/call';
import type { DisplayCall } from './CallHistoryCardTypes';
import { CallHistoryCallsSection } from './CallHistoryCallsSection';
import { CallHistoryCallDetailsDialog } from './CallHistoryCallDetailsDialog';
import { CallHistoryCaseDetailsDialog } from './CallHistoryCaseDetailsDialog';
import { CallHistoryCreateCaseDialog } from './CallHistoryCreateCaseDialog';
import {
  callHistoryItemToDisplayCall,
  getCaseStatusColor,
  getOutcomeColor,
  missedCallItemToDisplayCall,
} from './callHistoryUtils';

const CALLS_PER_PAGE = 20;

export function MissedCallsCard() {
  const { t } = useTranslation();
  const {
    useGetMissedCallsByUserQuery,
    useLazyGetCallByContactIdQuery,
    useGetCaseByIdQuery,
  } = api;

  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allCalls, setAllCalls] = useState<MissedCallItem[]>([]);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [selectedCall, setSelectedCall] = useState<DisplayCall | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const [selectedCaseContactId, setSelectedCaseContactId] = useState<
    string | null
  >(null);
  const [selectedCaseWorkspaceId, setSelectedCaseWorkspaceId] = useState<
    number | null
  >(null);
  const [isCreateCaseOpen, setIsCreateCaseOpen] = useState(false);
  const [loadingContactId, setLoadingContactId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const {
    data: callsResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetMissedCallsByUserQuery({
    limit: CALLS_PER_PAGE,
    cursor,
  });
  const [getCallByContactId] = useLazyGetCallByContactIdQuery();

  const {
    data: caseData,
    isLoading: isCaseLoading,
    error: caseError,
  } = useGetCaseByIdQuery(
    { caseId: selectedCaseId! },
    { skip: selectedCaseId === null }
  );

  useEffect(() => {
    if (!callsResponse?.calls) return;

    if (!hasLoadedInitial) {
      setAllCalls(callsResponse.calls);
      setHasLoadedInitial(true);
      return;
    }

    if (!cursor) return;
    setAllCalls(prev => {
      const existingIds = new Set(prev.map(call => call.contactId));
      const newCalls = callsResponse.calls.filter(
        call => !existingIds.has(call.contactId)
      );
      return [...prev, ...newCalls];
    });
  }, [callsResponse, cursor, hasLoadedInitial]);

  const hasMore = callsResponse?.hasMore ?? false;
  const nextCursor = callsResponse?.nextCursor ?? null;

  const displayCalls = useMemo(() => {
    return [...allCalls]
      .map(call => missedCallItemToDisplayCall(call))
      .sort(
        (a, b) =>
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      );
  }, [allCalls]);

  const refreshSelectedCall = useCallback(
    async (contactId: string) => {
      const callDetails = await getCallByContactId({ contactId }).unwrap();
      setSelectedCall(callHistoryItemToDisplayCall(callDetails));
    },
    [getCallByContactId]
  );

  const handleLoadMore = () => {
    if (!nextCursor) return;
    setCursor(nextCursor);
  };

  const handleReload = () => {
    setSelectedCall(null);
    setCursor(undefined);
    setAllCalls([]);
    setHasLoadedInitial(false);
    setLoadingContactId(null);
    void refetch();
  };

  const handleCallClick = async (call: DisplayCall) => {
    if (loadingContactId) return;
    setLoadingContactId(call.contactId);
    try {
      await refreshSelectedCall(call.contactId);
    } catch {
      setToast({
        message: t('components.contactCenter.errorLoadingCalls'),
        type: 'error',
      });
    } finally {
      setLoadingContactId(null);
    }
  };

  const handleCloseCallDialog = () => {
    setSelectedCall(null);
    setShowTranscript(false);
    setIsCreateCaseOpen(false);
  };

  const handleCloseCaseDialog = () => {
    setSelectedCaseId(null);
    setSelectedCaseContactId(null);
    setSelectedCaseWorkspaceId(null);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {t('components.contactCenter.missedCalls')} ({displayCalls.length})
          </Typography>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={handleReload}
            loading={isFetching}
            disabled={isFetching}
            sx={{ minWidth: 'auto', px: 1.25, py: 0.5, fontSize: '0.75rem' }}
          >
            {isFetching ? t('common.loading') : t('common.refresh')}
          </Button>
        </Box>

        <CallHistoryCallsSection
          displayCalls={displayCalls}
          isLoading={isLoading}
          hasLoadedInitial={hasLoadedInitial}
          error={error}
          isFetching={isFetching || loadingContactId !== null}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onCallClick={call => void handleCallClick(call)}
          onOpenCaseId={(caseId, contactId, workspaceId) => {
            setSelectedCaseId(caseId);
            setSelectedCaseContactId(contactId);
            setSelectedCaseWorkspaceId(workspaceId ?? null);
          }}
          getOutcomeColor={getOutcomeColor}
          getCaseStatusColor={getCaseStatusColor}
          emptyMessageKey="components.contactCenter.noMissedCallsFound"
          useUnknownCallerFallback
        />
      </Paper>

      <CallHistoryCallDetailsDialog
        selectedCall={selectedCall}
        showTranscript={showTranscript}
        setShowTranscript={value => setShowTranscript(value)}
        onClose={handleCloseCallDialog}
        getOutcomeColor={getOutcomeColor}
        getCaseStatusColor={getCaseStatusColor}
        onOpenCaseId={(caseId, contactId) => {
          setSelectedCaseId(caseId);
          setSelectedCaseContactId(contactId);
          setSelectedCaseWorkspaceId(
            selectedCall?.workspaceId ? Number(selectedCall.workspaceId) : null
          );
        }}
        onOpenCreateCase={() => setIsCreateCaseOpen(true)}
        enableCustomerLink
        isMissedCall
        onCustomerLinked={() => {
          if (!selectedCall) return;
          void refreshSelectedCall(selectedCall.contactId);
        }}
      />

      <CallHistoryCaseDetailsDialog
        selectedCaseId={selectedCaseId}
        contactId={selectedCaseContactId}
        workspaceId={selectedCaseWorkspaceId}
        onClose={handleCloseCaseDialog}
        isCaseLoading={isCaseLoading}
        caseError={caseError}
        caseData={caseData}
      />

      <CallHistoryCreateCaseDialog
        selectedCall={selectedCall}
        open={isCreateCaseOpen}
        onClose={() => setIsCreateCaseOpen(false)}
        useMissedCallDefaults
        onCreated={() => {
          if (!selectedCall) return;
          void refreshSelectedCall(selectedCall.contactId);
        }}
      />

      <Snackbar
        open={!!toast}
        onClose={() => setToast(null)}
        message={toast?.message}
        variant="toast"
        severity={toast?.type ?? 'info'}
        autoHideDuration={3000}
      />
    </>
  );
}
